import React, { useState } from 'react';
import {
  Box, IconButton, Tooltip, Stack, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Typography, TextField,
  Paper, InputAdornment, Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { useNavigate } from 'react-router-dom';
import DynamicDataGrid from '../components/DynamicDataGrid';
import { useDispatch } from 'react-redux';
import { deleteRecipient } from '../slices/recipientSlice';
import { useToast } from '../context/ToastContext';
import { motion } from 'framer-motion';

const MotionPaper = motion(Paper);

const FONT = 'Cairo, Tajawal, sans-serif';

// Shared styles for inline column search fields
const headerLabelSx = {
  fontWeight: 700,
  color: '#1E1B4B',
  fontFamily: FONT,
  fontSize: '0.8rem',
};
const searchFieldSx = {
  mt: 0.5,
  '& .MuiInput-root': { fontSize: '0.75rem' },
  '& input': { fontFamily: FONT, color: '#1E1B4B', py: 0.25, fontSize: '0.75rem' },
  '& input::placeholder': { color: '#9CA3AF', opacity: 1, fontSize: '0.75rem' },
};
const searchInputProps = {
  disableUnderline: true,
  startAdornment: (
    <InputAdornment position="start" sx={{ mr: 0.5 }}>
      <SearchIcon sx={{ fontSize: 15, color: '#9CA3AF' }} />
    </InputAdornment>
  ),
};

/**
 * Controlled table: filtering/pagination state lives in RecipientPage (which
 * owns the server-side fetch). This component only renders the grid + the
 * date filter card and reports filter/pagination changes upward.
 */
const RecipientTable = ({
  data,
  rowCount,
  loading,
  paginationModel,
  onPaginationModelChange,
  filterValues,
  onFilterChange,
  onDeleted,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);

  const handleView = (id) => {
    navigate(`/recipient/view/${id}`);
  };

  const handleDeleteClick = (id) => {
    setSelectedPaymentId(id);
    setOpenDialog(true);
  };

  const handleConfirmDelete = () => {
    setOpenDialog(false);
    dispatch(deleteRecipient(selectedPaymentId))
      .unwrap()
      .then(() => {
        showToast('تم حذف السند بنجاح', 'success');
        if (onDeleted) onDeleted();
      })
      .catch(() => {
        showToast('فشل حذف السند', 'error');
      });
  };

  const handleCancelDelete = () => {
    setOpenDialog(false);
  };

  // جلب الدور من localStorage
  const userRole = localStorage.getItem('role');

  const columns = [
    {
      field: 'number',
      headerName: 'رقم السند',
      flex: 1,
      renderHeader: () => (
        <Box>
          <Typography sx={headerLabelSx}>رقم السند</Typography>
          <TextField
            variant="standard"
            value={filterValues.number}
            onChange={(e) => onFilterChange('number', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={searchInputProps}
            sx={searchFieldSx}
          />
        </Box>
      )
    },
    {
      field: 'student_name',
      headerName: 'اسم الطالب',
      flex: 1,
      renderHeader: () => (
        <Box>
          <Typography sx={headerLabelSx}>اسم الطالب</Typography>
          <TextField
            variant="standard"
            value={filterValues.student_name}
            onChange={(e) => onFilterChange('student_name', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={searchInputProps}
            sx={searchFieldSx}
          />
        </Box>
      )
    },
    {
      field: 'parent_name',
      headerName: 'اسم ولي الأمر',
      flex: 1,
      renderHeader: () => (
        <Box>
          <Typography sx={headerLabelSx}>اسم ولي الأمر</Typography>
          <TextField
            variant="standard"
            value={filterValues.parent_name}
            onChange={(e) => onFilterChange('parent_name', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={searchInputProps}
            sx={searchFieldSx}
          />
        </Box>
      )
    },
    {
      field: 'parent_phone',
      headerName: 'الهاتف',
      flex: 1,
      renderHeader: () => (
        <Box>
          <Typography sx={headerLabelSx}>الهاتف</Typography>
          <TextField
            variant="standard"
            value={filterValues.parent_phone}
            onChange={(e) => onFilterChange('parent_phone', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={searchInputProps}
            sx={searchFieldSx}
          />
        </Box>
      )
    },
    {
      field: 'class_name',
      headerName: 'الصف',
      flex: 1,
      renderHeader: () => (
        <Box>
          <Typography sx={headerLabelSx}>الصف</Typography>
          <TextField
            variant="standard"
            value={filterValues.class_name}
            onChange={(e) => onFilterChange('class_name', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={searchInputProps}
            sx={searchFieldSx}
          />
        </Box>
      )
    },
    { field: 'amount', headerName: 'المبلغ (شيكل)', flex: 0.8 },
    { field: 'date', headerName: 'تاريخ الدفع', flex: 0.8 },
    {
      field: 'payment_type',
      headerName: 'طريقة الدفع',
      flex: 1,
      renderHeader: () => (
        <Box>
          <Typography sx={headerLabelSx}>طريقة الدفع</Typography>
          <TextField
            variant="standard"
            value={filterValues.method}
            onChange={(e) => onFilterChange('method', e.target.value)}
            placeholder="بحث"
            size="small"
            InputProps={searchInputProps}
            sx={searchFieldSx}
          />
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: 'الإجراءات',
      flex: 1.5,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Stack useFlexGap direction="row" spacing={1} justifyContent="center" width="100%">
          <Tooltip title="عرض التفاصيل">
            <IconButton
              size="small"
              onClick={() => handleView(params.row.id)}
              sx={{
                color: '#4338CA',
                bgcolor: '#EEF2FF',
                borderRadius: 2,
                '&:hover': { bgcolor: '#4338CA', color: '#fff' },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {userRole === 'manager' && (
            <Tooltip title="حذف الدفعة">
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(params.row.id)}
                sx={{
                  color: '#DC2626',
                  bgcolor: '#FEE2E2',
                  borderRadius: 2,
                  '&:hover': { bgcolor: '#DC2626', color: '#fff' },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )
    }
  ];

  return (
    // 🎨 Design System Applied — summary cards live in RecipientPage; this renders filter + grid + dialog
    <Box sx={{ direction: 'rtl', fontFamily: FONT, p: { xs: 1.5, md: 2 }, overflowX: 'auto', maxWidth: '100vw' }}>
      {/* فلتر التاريخ */}
      <MotionPaper
        component={motion.div}
        elevation={0}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.2, ease: 'easeOut' }}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          borderRadius: 3,
          bgcolor: '#fff',
          boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
          border: '1px solid #E5E7EB',
        }}
      >
        <Stack useFlexGap direction="row" alignItems="center" spacing={1} mb={2}>
          <EventNoteIcon sx={{ fontSize: 20, color: '#4338CA' }} />
          <Typography sx={{ fontWeight: 700, color: '#1E1B4B', fontFamily: FONT }}>
            تصفية حسب التاريخ
          </Typography>
        </Stack>
        <Divider sx={{ mb: 2, borderColor: '#E5E7EB' }} />
        <Stack useFlexGap direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <TextField
            type="date"
            label="من"
            variant="outlined"
            value={filterValues.dateFrom}
            onChange={(e) => onFilterChange('dateFrom', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{
              minWidth: 140,
              '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: FONT },
              '& label': { fontFamily: FONT },
            }}
          />
          <TextField
            type="date"
            label="إلى"
            variant="outlined"
            value={filterValues.dateTo}
            onChange={(e) => onFilterChange('dateTo', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{
              minWidth: 140,
              '& .MuiOutlinedInput-root': { borderRadius: 2, fontFamily: FONT },
              '& label': { fontFamily: FONT },
            }}
          />
        </Stack>
      </MotionPaper>

      <DynamicDataGrid
        columns={columns}
        rows={data}
        loading={loading}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        rowCount={rowCount}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />

      {/* Dialog for confirming delete */}
      <Dialog
        open={openDialog}
        onClose={handleCancelDelete}
        dir="rtl"
        PaperProps={{
          sx: {
            borderRadius: 3,
            fontFamily: FONT,
            boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800, color: '#1E1B4B', fontFamily: FONT }}>
          تأكيد الحذف
        </DialogTitle>
        <DialogContent sx={{ color: '#6B7280', fontFamily: FONT }}>
          هل أنت متأكد أنك تريد حذف هذه الدفعة؟
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDelete}
            sx={{
              color: '#6B7280',
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 700,
              fontFamily: FONT,
              '&:hover': { bgcolor: '#F3F4F6' },
            }}
          >
            إلغاء
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            sx={{
              bgcolor: '#DC2626',
              borderRadius: 2.5,
              textTransform: 'none',
              fontWeight: 700,
              fontFamily: FONT,
              boxShadow: 'none',
              '&:hover': { bgcolor: '#B91C1C', boxShadow: 'none' },
            }}
          >
            حذف
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RecipientTable;
